"use client";
import NotFound from "@/assets/images/not-found.png";
import { MagnifyingGlass, Info } from "@phosphor-icons/react/dist/ssr";
import CustomTooltip from "@/components/CustomTooltip";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useState, useEffect, useCallback } from "react";
import {
  getCurrentUnicornsByCity,
  getUnicornStatsByCountry,
} from "@/services/api/UnicornTracker";
import Image from "next/image";
import Empty from "@/components/empty-states/empty";

export default function HighestUnicorn({
  regionData,
  countryData,
  selectedCountryName,
  selectedRegionName,
}) {
  const [countryRankings, setCountryRankings] = useState([]);
  const [cityRankings, setCityRankings] = useState([]);
  const [cityTotals, setCityTotals] = useState({
    cities: 0,
    unicorns: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const selectedCountryId = searchParams.get("country");
  const selectedRegionId = searchParams.get("unicorn_regions");
  const sectors = searchParams.get("sectors");
  const stages = searchParams.get("stages");
  const valuationCondition = searchParams.get("valuation_condition");
  const valuationAmount = searchParams.get("valuation_amount");
  const yearFoundedCondition = searchParams.get("year_founded_condition");
  const yearFoundedYear = searchParams.get("year_founded_year");
  const timeToUnicornMin = searchParams.get("min_time_to_unicorn");
  const timeToUnicornMax = searchParams.get("max_time_to_unicorn");
  const unicornStatus = searchParams.get("unicorn_status");

  const applyStatusParams = useCallback((params) => {
    const statuses = unicornStatus
      ? unicornStatus.split(",").filter(Boolean)
      : ["current", "fallen", "alumni"];

    params.unicorn_status = statuses.join(",");
    if (statuses.includes("fallen")) params.include_lost_unicorn = 1;
    if (statuses.includes("alumni")) params.include_alumni = 1;
    if (!statuses.includes("current")) params.exclude_current_unicorn = 1;
  }, [unicornStatus]);

  const updateQuery = (key, value) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) params.set(key, value);
    else params.delete(key);
    router.push(`${pathname}?${params.toString()}`);
  };

  useEffect(() => {
    let isActive = true;

    async function fetchCountryRankings() {
      try {
        setLoading(true);
        setCountryRankings([]);

        const params = {};
        if (selectedRegionId) params.unicorn_regions = selectedRegionId;
        if (sectors) params.sectors = sectors;
        if (stages) params.stages = stages;

        if (valuationCondition && valuationAmount) {
          params.valuation_condition = valuationCondition;
          params.valuation_amount = valuationAmount;
        }

        if (yearFoundedCondition && yearFoundedYear) {
          params.year_founded_condition = yearFoundedCondition;
          params.year_founded_year = yearFoundedYear;
        }

        if (timeToUnicornMin) params.min_time_to_unicorn = timeToUnicornMin;
        if (timeToUnicornMax) params.max_time_to_unicorn = timeToUnicornMax;

        applyStatusParams(params);

        const statsRes = await getUnicornStatsByCountry({ params });

        const countryStats = statsRes.data || [];
        const sortedCountries = countryStats
          .sort((a, b) => b.count - a.count)
          .map((country) => ({
            country_id: country.id,
            country_name: country.name,
            unicorn_count: country.count,
            total_valuation: country.total_valuation,
          }));

        if (isActive) setCountryRankings(sortedCountries);
      } catch (error) {
        console.error("Error fetching country rankings:", error);
        if (isActive) setCountryRankings([]);
      } finally {
        if (isActive) setLoading(false);
      }
    }

    if (!selectedCountryId) fetchCountryRankings();
    else setCountryRankings([]);

    return () => {
      isActive = false;
    };
  }, [
    selectedRegionId,
    selectedCountryId,
    sectors,
    stages,
    valuationCondition,
    valuationAmount,
    yearFoundedCondition,
    yearFoundedYear,
    timeToUnicornMin,
    timeToUnicornMax,
    applyStatusParams,
  ]);

  useEffect(() => {
    let isActive = true;

    async function fetchCityRankings() {
      try {
        setLoading(true);
        setCityRankings([]);
        setCityTotals({ cities: 0, unicorns: 0 });

        const params = {};
        if (sectors) params.sectors = sectors;
        if (stages) params.stages = stages;

        if (valuationCondition && valuationAmount) {
          params.valuation_condition = valuationCondition;
          params.valuation_amount = valuationAmount;
        }

        if (yearFoundedCondition && yearFoundedYear) {
          params.year_founded_condition = yearFoundedCondition;
          params.year_founded_year = yearFoundedYear;
        }

        if (timeToUnicornMin) params.min_time_to_unicorn = timeToUnicornMin;
        if (timeToUnicornMax) params.max_time_to_unicorn = timeToUnicornMax;

        applyStatusParams(params);

        const citiesRes = await getCurrentUnicornsByCity(selectedCountryId, {
          params,
        });
        const cityStats = citiesRes.data?.data?.cities ?? [];
        const totalCities = citiesRes.data?.data?.total_cities ?? 0;
        const totalUnicorns = citiesRes.data?.data?.total_unicorns ?? 0;

        const sortedCities = cityStats
          .sort((a, b) => b.unicorn_count - a.unicorn_count)
          .map((city) => ({
            city_id: city.city_id,
            city_name: city.city_name,
            unicorn_count: city.unicorn_count,
            total_valuation: city.total_valuation,
            total_valuation_display: city.total_valuation_display,
          }));

        if (isActive) {
          setCityRankings(sortedCities);
          setCityTotals({
            cities: totalCities || sortedCities.length,
            unicorns: totalUnicorns,
          });
        }
      } catch (error) {
        console.error("Error fetching city rankings:", error);
        if (isActive) setCityRankings([]);
      } finally {
        if (isActive) setLoading(false);
      }
    }

    if (selectedCountryId) fetchCityRankings();
    else {
      setCityRankings([]);
      setCityTotals({ cities: 0, unicorns: 0 });
    }

    return () => {
      isActive = false;
    };
  }, [
    selectedCountryId,
    sectors,
    stages,
    valuationCondition,
    valuationAmount,
    yearFoundedCondition,
    yearFoundedYear,
    timeToUnicornMin,
    timeToUnicornMax,
    applyStatusParams,
  ]);

  // Reset search when switching country
  useEffect(() => {
    setSearchTerm("");
  }, [selectedCountryId]);

  // Search rules:
  // - Cities only
  // - Only show search if TOTAL cities > 8
  const isCityMode = !!selectedCountryId;
  const showCitySearchBar = isCityMode && !loading && cityTotals.cities > 8;

  // If search bar hidden, don't filter
  const effectiveCitySearchTerm = showCitySearchBar ? searchTerm : "";

  const filteredCountries = countryRankings.filter((country) => {
    if (!country.country_name) return false;
    return country.country_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
  });

  const filteredCities = cityRankings.filter((city) => {
    if (!city.city_name) return false;
    return city.city_name
      .toLowerCase()
      .includes(effectiveCitySearchTerm.toLowerCase());
  });

  const formatValuation = (valuation) => {
    if (!valuation || valuation === 0) return "N/A";

    const billions = valuation / 1000000000;

    if (billions >= 1000) return `$${(billions / 1000).toFixed(1)}T`;
    if (billions >= 1) return `$${billions.toFixed(1)}B`;
    return `$${(billions * 1000).toFixed(0)}M`;
  };

  const getCountryFlag = (countryName) => {
    const flagMap = {
      "United States": "fi-us",
      China: "fi-cn",
      India: "fi-in",
      "United Kingdom": "fi-gb",
      Germany: "fi-de",
      France: "fi-fr",
      Canada: "fi-ca",
      Israel: "fi-il",
      Singapore: "fi-sg",
      "South Korea": "fi-kr",
      Japan: "fi-jp",
      Australia: "fi-au",
      Brazil: "fi-br",
      Netherlands: "fi-nl",
      Sweden: "fi-se",
      Switzerland: "fi-ch",
      UAE: "fi-ae",
      "Saudi Arabia": "fi-sa",
      Egypt: "fi-eg",
      "South Africa": "fi-za",

      Argentina: "fi-ar",
      Austria: "fi-at",
      Belgium: "fi-be",
      Chile: "fi-cl",
      Colombia: "fi-co",
      "Czech Republic": "fi-cz",
      Denmark: "fi-dk",
      Estonia: "fi-ee",
      Finland: "fi-fi",
      "Hong Kong": "fi-hk",
      Hungary: "fi-hu",
      Indonesia: "fi-id",
      Ireland: "fi-ie",
      Italy: "fi-it",
      Latvia: "fi-lv",
      Lithuania: "fi-lt",
      Luxembourg: "fi-lu",
      Malaysia: "fi-my",
      Mexico: "fi-mx",
      "New Zealand": "fi-nz",
      Nigeria: "fi-ng",
      Norway: "fi-no",
      Poland: "fi-pl",
      Portugal: "fi-pt",
      Romania: "fi-ro",
      Russia: "fi-ru",
      Slovenia: "fi-si",
      Spain: "fi-es",
      Taiwan: "fi-tw",
      Thailand: "fi-th",
      Turkey: "fi-tr",
      Ukraine: "fi-ua",
      Uruguay: "fi-uy",
      Venezuela: "fi-ve",
      Vietnam: "fi-vn",
      Senegal: "fi-sn",
      Qatar: "fi-qa",
      Seychelles: "fi-sc",

      USA: "fi-us",
      UK: "fi-gb",
      "United Arab Emirates": "fi-ae",
    };

    return flagMap[countryName] || "fi-xx";
  };

  return (
    <>
      <div className='flex items-center justify-between'>
        <h2 className='text-sm font-medium'>
          {selectedCountryId ? "Top Cities" : "Top Countries"}
        </h2>
        <CustomTooltip
          trigger={
            <span className='-mt-2 -mr-2 text-white/40'>
              <Info className='size-5' />
            </span>
          }
          content='Ranked by number of tracked unicorns matching your filters. Combined value is the sum of last known private valuations. Based on current HQ.'
        />
      </div>

      {/* CITY ONLY: show search bar only if TOTAL cities > 8 */}
      {showCitySearchBar && (
        <form
          className='relative flex w-full mt-3 lg:flex-1'
          onSubmit={(e) => e.preventDefault()}
        >
          <div className='absolute inset-y-0 left-0 flex items-center w-full pl-2.5 border-none pointer-events-none text-white/40 sm:text-sm'>
            <MagnifyingGlass
              className='w-4 h-4 text-white/40'
              aria-hidden='true'
            />
          </div>

          <input
            type='text'
            placeholder='Search cities...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full p-3 text-xs text-white transition-colors duration-200 border rounded-md h-9 border-white/5 focus:outline-none placeholder:text-white/40 pl-9 hover:bg-white/10 bg-white/5'
          />
        </form>
      )}

      <div className='pt-4 -my-2.5 divide-y divide-white/10 -mx-4'>
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className='flex items-center py-2.5 animate-pulse'>
              <div className='flex items-center flex-1 gap-3'>
                <div className='rounded-full size-9 bg-white/10' />
                <div>
                  <div className='w-16 h-3 mb-1 rounded bg-white/10' />
                  <div className='w-20 h-2 rounded bg-white/10' />
                </div>
              </div>
              <div className='w-12 h-3 rounded bg-white/10' />
            </div>
          ))
        ) : selectedCountryId ? (
          cityRankings.length === 0 ? (
            <div className='mx-2 mb-2 -mt-2'>
              <Empty
                title='Data Not Available'
                desc='No unicorn cities for this selection.'
              />
            </div>
          ) : filteredCities.length > 0 ? (
            filteredCities.slice(0, 8).map((city, index) => (
              <div
                key={city.city_id || index}
                className='flex items-center w-full py-2.5 px-4 pl-1'
              >
                <div className='flex items-center flex-1 gap-3'>
                  <div className='text-center font-semibold text-white min-w-[2rem]'>
                    #{index + 1}
                  </div>

                  <div className='flex-1 text-left'>
                    <p className='text-sm font-medium text-white'>
                      {city.city_name || "Unknown City"}
                    </p>
                    <p className='mt-0.5 text-[11px] text-white/50'>
                      Combined Value:{" "}
                      {city.total_valuation_display ||
                        formatValuation(city.total_valuation)}
                    </p>
                  </div>
                </div>

                <div className='text-right'>
                  <p className='text-lg font-bold leading-none text-white'>
                    {city.unicorn_count || 0}
                  </p>
                  <p className='text-[11px] mt-0.5 text-white/50'>
                    {(city.unicorn_count || 0) === 1 ? "Unicorn" : "Unicorns"}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className='mx-2 mb-2 -mt-2'>
              <Empty
                title='Data Not Available'
                desc='No cities found for this selection.'
              />
            </div>
          )
        ) : filteredCountries.length > 0 ? (
          filteredCountries.slice(0, 5).map((country, index) => (
            <button
              key={country.country_id || index}
              onClick={() =>
                updateQuery("country", country.country_id?.toString() || "")
              }
              className='flex items-center w-full py-2.5 hover:bg-white/5 transition-colors px-4 pl-1'
            >
              <div className='flex items-center flex-1 gap-3'>
                <div className='font-semibold text-white min-w-[2rem]'>
                  #{index + 1}
                </div>

                <div className='relative inline-flex items-center justify-center overflow-hidden rounded-full size-9 bg-white/5 ring-[3px] ring-white/15'>
                  <span
                    className={`absolute inset-0 bg-cover fib ${getCountryFlag(
                      country.country_name,
                    )} fis`}
                  />
                </div>

                <div className='flex-1 text-left'>
                  <p className='text-sm font-medium text-white'>
                    {country.country_name || "Unknown Country"}
                  </p>
                  <p className='mt-0.5 text-[11px] text-white/50'>
                    Combined Value:{" "}
                    {formatValuation(country.total_valuation)}
                  </p>
                </div>
              </div>

              <div className='text-right'>
                <p className='text-lg font-bold leading-none text-white'>
                  {country.unicorn_count || 0}
                </p>
                <p className='text-[11px] mt-0.5 text-white/50'>
                  {(country.unicorn_count || 0) === 1 ? "Unicorn" : "Unicorns"}
                </p>
              </div>
            </button>
          ))
        ) : (
          <div className='py-4 text-center'>
            <p className='text-xs text-white/50'>
              {searchTerm ? "No countries found" : "No data available"}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
